import type { ConsultationResponseDto } from '@montugroup/pms-api-contracts';
import { useEffect, useState } from 'react';

import useGetConsultations from './useGetConsultations';

export const BOOKED_CONSULTATION_LOOKUP_TIMEOUT_MS = 3000;
export const BOOKED_CONSULTATION_RETRY_INTERVAL_MS = 500;

export interface BookedConsultationLookup {
  /** the booking uid from the cal.com event, when it has one */
  uid?: string;
  /** every calcomBookingUuid in the upcoming list when the event fired, or null if the list hadn't loaded */
  knownUids: string[] | null;
}

export type BookedConsultationResult =
  | { status: 'searching'; consultation: null }
  | { status: 'found'; consultation: ConsultationResponseDto }
  | { status: 'timedOut'; consultation: null };

// An unknown event uid names the new booking, as another new one may have been made elsewhere. A known uid (a
// reschedule's old booking, Scheduled until the webhook lands) or none falls back to any new consultation.
const findNewConsultation = (consultations: ConsultationResponseDto[], lookup: BookedConsultationLookup) => {
  // without a baseline every consultation looks new, so only an exact uid match is safe
  if (!lookup.knownUids) {
    return lookup.uid
      ? consultations.find((consultation) => consultation.calcomBookingUuid === lookup.uid) ?? null
      : null;
  }
  const knownUidSet = new Set(lookup.knownUids);
  const newConsultations = consultations.filter(
    (consultation) => consultation.calcomBookingUuid && !knownUidSet.has(consultation.calcomBookingUuid),
  );
  if (lookup.uid && !knownUidSet.has(lookup.uid)) {
    return newConsultations.find((consultation) => consultation.calcomBookingUuid === lookup.uid) ?? null;
  }
  return newConsultations[0] ?? null;
};

const SEARCHING: BookedConsultationResult = { status: 'searching', consultation: null };
const TIMED_OUT: BookedConsultationResult = { status: 'timedOut', consultation: null };

/**
 * Finds the consultation a cal.com booking or reschedule just created, polling the upcoming list until it appears or
 * the timeout passes, as pms-service only creates it when the cal.com webhook lands, which can be after the event.
 * @param lookup - the booking's uid from the event and the uids already in the list
 * @returns the consultation once it's found, or whether the lookup is still searching or has timed out
 */
export function useFindBookedConsultation(lookup: BookedConsultationLookup): BookedConsultationResult {
  // with no baseline and no uid nothing can tell the new consultation apart, so don't poll for it
  const [result, setResult] = useState(lookup.knownUids || lookup.uid ? SEARCHING : TIMED_OUT);
  const isSearching = result.status === 'searching';
  // the same list the page takes knownUids from, so they're compared against one baseline; every card's cal.com listener
  // refetches it on the event, so the first poll can wait an interval
  const { consultData } = useGetConsultations({
    when: 'upcoming',
    refetchInterval: isSearching ? BOOKED_CONSULTATION_RETRY_INTERVAL_MS : false,
  });

  // keep the consultation as it was when found: joining the waitlist refetches the list, and the prompt must not
  // change under the patient
  const consultation = isSearching ? findNewConsultation(consultData, lookup) : null;
  if (consultation) {
    setResult({ status: 'found', consultation });
  }

  useEffect(() => {
    if (!isSearching) {
      return undefined;
    }
    const deadline = setTimeout(
      () => setResult((current) => (current.status === 'searching' ? TIMED_OUT : current)),
      BOOKED_CONSULTATION_LOOKUP_TIMEOUT_MS,
    );
    return () => clearTimeout(deadline);
  }, [isSearching]);

  return result;
}
